<script lang="ts">
import type { OrganisationOption } from '@/components/design/domain/organisation/organisationOption';

/** What the list needs of an organisation — the row shows name and description. */
export type HomeOrganisationOption = OrganisationOption;
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaListItem from '@/components/design/composite/GaiaListItem.vue';

/**
 * Home for a signed-in user who is in no organisation. Says so, lists the
 * organisations they can switch into, and — when the deployment allows
 * self-service — offers to create one. Stateless: the view owns the switch and
 * the create dialog, so the workbench can show every state from fixtures.
 */
const props = defineProps({
  organisations: {
    type: Array as PropType<HomeOrganisationOption[]>,
    required: true,
  },
  /** `userPermissions.can_create_organisation` — a deployment setting. */
  canCreate: {
    type: Boolean,
    default: false,
  },
  /** The organisation a switch is in flight for; its row shows a spinner. */
  switchingUid: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ select: [uid: string]; create: [] }>();

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}
</script>

<template>
  <!-- One narrow column, like the composer it replaces: this is a short page
       with one thing to do, not a dashboard. -->
  <n-flex vertical :size="24" :style="{ width: '100%', maxWidth: '560px' }">
    <n-flex v-if="props.organisations.length" vertical :size="12">
      <gaia-section-header>Your organisations</gaia-section-header>

      <n-flex vertical :size="8">
        <gaia-list-item
          v-for="organisation in props.organisations"
          :key="organisation.uid"
          variant="card"
          size="item"
          :clickable="!props.switchingUid"
          @click="!props.switchingUid && emit('select', organisation.uid)"
        >
          <template #leading>
            <gaia-avatar variant="mark">{{ initialOf(organisation.name) }}</gaia-avatar>
          </template>
          <gaia-text weight="semibold">{{ organisation.name }}</gaia-text>
          <template v-if="organisation.description" #footer>
            <gaia-text tone="subtle" truncate>{{ organisation.description }}</gaia-text>
          </template>
          <template #trailing>
            <gaia-spinner v-if="props.switchingUid === organisation.uid" size="small" />
            <template v-else>Switch</template>
          </template>
        </gaia-list-item>
      </n-flex>

      <!-- Beside a list of choices the create is one option among them, so it
           takes the quiet variant; alone in the empty state it is the action. -->
      <n-flex v-if="props.canCreate" justify="center" :style="{ marginTop: '4px' }">
        <gaia-create-button subject="organisation" variant="default" @click="emit('create')" />
      </n-flex>
    </n-flex>

    <!-- One box that names only the ways in this user actually has: an
         invitation from an administrator, and — when the deployment allows
         self-service — creating an organisation of their own. -->
    <gaia-empty-state v-else dashed title="You are not part of an organisation yet">
      <template v-if="props.canCreate">
        To start working, ask an administrator to invite you or create a new organisation.
      </template>
      <template v-else>To start working, ask an administrator to invite you.</template>
      <template v-if="props.canCreate" #actions>
        <gaia-create-button subject="organisation" @click="emit('create')" />
      </template>
    </gaia-empty-state>
  </n-flex>
</template>
